import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

const MemberLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        // Check if user has a profile and get membership info
        const { data: profile, error: profileError } = await supabase
          .from('member_profiles_2025_12_03_10_30')
          .select(`
            *,
            membership_types_2025_12_03_10_30 (
              name,
              price_eur
            )
          `)
          .eq('user_id', data.user.id)
          .single();

        // If no profile exists, create one
        if (profileError && profileError.code === 'PGRST116') {
          const { error: insertError } = await supabase
            .from('member_profiles_2025_12_03_10_30')
            .insert({
              user_id: data.user.id,
              full_name: data.user.user_metadata?.full_name || '',
              email: data.user.email || '',
              membership_status: 'pending',
              payment_status: 'unpaid'
            });
          
          if (!insertError) {
            navigate('/member-dashboard');
            return;
          }
        }

        if (profile?.is_admin) {
          navigate('/admin-dashboard');
        } else if (profile?.membership_type_id) {
          // Navigate to appropriate member portal based on membership type
          const membershipName = profile.membership_types_2025_12_03_10_30?.name;
          const portalMap: { [key: string]: string } = {
            'Student Member': '/student-portal',
            'Academic Member': '/academic-portal',
            'Professional Member': '/professional-portal',
            'Chartered Member': '/chartered-portal',
            'Training Centre': '/training-centre-portal',
            'Corporate Bronze': '/bronze-portal',
            'Corporate Silver': '/silver-portal',
            'Corporate Gold': '/gold-portal'
          };
          navigate(portalMap[membershipName || ''] || '/member-dashboard');
        } else {
          navigate('/member-dashboard');
        }
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Shield className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-2xl">Member Login</CardTitle>
            <CardDescription>
              Access your CSI member portal and exclusive resources
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-6 text-center space-y-2">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link to="/join" className="text-primary hover:underline">
                  Join CSI
                </Link>
              </p>
              <p className="text-sm text-muted-foreground">
                <Link to="/forgot-password" className="text-primary hover:underline">
                  Forgot your password?
                </Link>
              </p>
              <p className="text-sm text-muted-foreground">
                Administrator?{' '}
                <Link to="/admin-login" className="text-primary hover:underline">
                  Admin Login
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MemberLogin;